import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Sparkles, Wallet } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { studio, apiError, eur, inputCls, btnPrimary, btnGhost, TRADE_OPTIONS, sym } from "@/lib/studioApi";
import { ListSkeleton } from "@/components/studio/StudioUI";

const empty = { name: "", description: "", unit: "forfait", price_htva: 0, vat_percent: 21, cost_price: 0, duration_minutes: 0, trade: "chauffagiste" };
const UNITS = ["forfait", "heure", "pièce", "mètre", "m²", "jour"];

export default function StudioLibrary() {
  const [services, setServices] = useState(null);
  const [tab, setTab] = useState("tous");
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);
  const [isOwner, setIsOwner] = useState(true);

  const load = useCallback(() => {
    studio.get("/services").then(({ data }) => setServices(data)).catch(() => setServices([]));
    studio.get("/whoami").then(({ data }) => setIsOwner(!!data.is_owner)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await studio.post("/services", {
        ...form,
        price_htva: Number(form.price_htva) || 0,
        vat_percent: Number(form.vat_percent) || 0,
        cost_price: Number(form.cost_price) || 0,
        duration_minutes: Number(form.duration_minutes) || 0,
      });
      toast.success("Prestation ajoutée.");
      setForm(empty);
      setShowForm(false);
      load();
    } catch (e) { toast.error(apiError(e, "Enregistrement impossible.")); }
  };

  const remove = async (s) => {
    try {
      await studio.delete(`/services/${s.id}`);
      load();
    } catch (e) { toast.error("Suppression impossible."); }
  };

  const seed = async (trade) => {
    try {
      const { data } = await studio.post(`/services/seed?trade=${trade}`);
      toast.success(data.created ? `${data.created} prestations ajoutées.` : "Déjà à jour.");
      load();
    } catch (e) { toast.error("Import impossible."); }
  };

  const seedInterventions = async () => {
    try {
      const { data } = await studio.post("/services/seed-interventions");
      toast.success(data.created ? `${data.created} tarifs d'intervention ajoutés.` : "Ces tarifs sont déjà présents.");
      load();
    } catch (e) { toast.error(apiError(e, "Import impossible.")); }
  };

  const fillCosts = async () => {
    try {
      const { data } = await studio.post("/services/fill-costs");
      toast.success(data.updated ? `${data.updated} prestations complétées.` : "Vos coûts sont déjà renseignés.");
      load();
    } catch (e) { toast.error(apiError(e, "Complétion impossible.")); }
  };

  const saveField = async (s, field, value) => {
    const next = Number(value) || 0;
    if (next === Number(s[field] || 0)) return;
    setServices((list) => (list || []).map((x) => (x.id === s.id ? { ...x, [field]: next } : x)));
    try {
      await studio.put(`/services/${s.id}`, { ...s, [field]: next });
    } catch (e) {
      toast.error(apiError(e, "Enregistrement impossible."));
      load();
    }
  };

  const margin = (s) => (s.cost_price ? `${Math.round(((s.price_htva - s.cost_price) / s.price_htva) * 100)} %` : "—");

  return (
    <StudioLayout
      title="Bibliothèque de prix"
      subtitle="Votre bibliothèque de prestations : l'assistant l'utilise pour chiffrer vos devis avec VOS prix."
      action={<button type="button" data-testid="new-service-btn" onClick={() => setShowForm(!showForm)} className={btnPrimary}><Plus className="w-4 h-4" /> Nouvelle prestation</button>}
    >
      {showForm && (
        <div data-testid="service-form" className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)] space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="service-name" className={inputCls} placeholder="Nom (ex. : Heure de main-d'œuvre) *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <select data-testid="service-trade" className={inputCls} value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })}>
              {TRADE_OPTIONS.map(([v, l]) => <option key={v} value={v} >{l}</option>)}
            </select>
            <select data-testid="service-unit" className={inputCls} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
              {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
            <input data-testid="service-price" type="number" step="0.01" className={inputCls} placeholder="Prix HTVA" value={form.price_htva} onChange={(e) => setForm({ ...form, price_htva: e.target.value })} />
            <select data-testid="service-vat" className={inputCls} value={form.vat_percent} onChange={(e) => setForm({ ...form, vat_percent: e.target.value })}>
              {[0, 6, 12, 21].map((v) => <option key={v} value={v}>{`${v} % TVA`}</option>)}
            </select>
            <input data-testid="service-cost" type="number" step="0.01" className={inputCls} placeholder="Coût fournisseur (facultatif)" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: e.target.value })} />
            <input data-testid="service-duration" type="number" className={inputCls} placeholder="Durée estimée (minutes)" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} />
          </div>
          <textarea data-testid="service-description" rows={2} className={inputCls} placeholder="Description standard reprise dans le devis" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex gap-2">
            <button type="button" data-testid="service-save-btn" disabled={!form.name.trim()} onClick={create} className={btnPrimary}>Enregistrer</button>
            <button type="button" onClick={() => setShowForm(false)} className={btnGhost}>Annuler</button>
          </div>
        </div>
      )}

      <div className="mt-6 flex flex-wrap gap-2">
        <button type="button" data-testid="seed-chauffagiste" onClick={() => seed("chauffagiste")} className={btnGhost}>
          <Sparkles className="w-3.5 h-3.5" /> Importer les tarifs types chauffagiste
        </button>
        <button type="button" data-testid="seed-electricien" onClick={() => seed("electricien")} className={btnGhost}>
          <Sparkles className="w-3.5 h-3.5" /> Importer les tarifs types électricien
        </button>
        <button type="button" data-testid="seed-entrepreneur" onClick={() => seed("entrepreneur_general")} className={btnGhost}>
          <Sparkles className="w-3.5 h-3.5" /> Importer les tarifs types entrepreneur général
        </button>
        <button type="button" data-testid="seed-interventions" onClick={() => seedInterventions()} className={btnGhost}>
          <Sparkles className="w-3.5 h-3.5" /> Importer les tarifs de dépannage et entretien
        </button>
        <button type="button" data-testid="fill-costs-btn" onClick={fillCosts} className={btnGhost}>
          <Wallet className="w-3.5 h-3.5" /> Compléter mes coûts d'achat et durées
        </button>
      </div>

      <p className="mt-3 text-xs text-[#5C6A87] leading-relaxed">
        Le coût d'achat et la durée servent à calculer votre marge et votre temps sur chaque devis. Vous pouvez les
        modifier directement dans la liste : c'est enregistré dès que vous quittez le champ.
      </p>

      <div className="mt-5 -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0">
        {[["tous", "Tous"], ["devis", "Devis / chantiers"], ["intervention", "Dépannages & entretiens"]].map(([v, l]) => (
          <button
            key={v}
            type="button"
            data-testid={`library-tab-${v}`}
            onClick={() => setTab(v)}
            className={`rounded-xl border px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors duration-200 ${
              tab === v ? "border-[#3A5CC8] bg-[#EEF1FC] text-[#3A5CC8]" : "border-[#E2E7F7] bg-white text-[#5C6A87] hover:border-[#E2E7F7]"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {services === null && <ListSkeleton />}
        {services !== null && services.length === 0 && <p className="text-sm text-[#5C6A87]">Aucune prestation. Importez un jeu de tarifs types pour démarrer.</p>}
        {(services || []).filter((s) => tab === "tous" || (s.category || "devis") === tab).map((s) => (
          <div key={s.id} data-testid={`service-${s.id}`} className="rounded-2xl border border-[#E2E7F7] bg-white px-4 py-3 sm:px-5 sm:py-4 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-[#0F2645] break-words">{s.name}</p>
                {s.description && <p className="mt-0.5 text-xs text-[#5C6A87] truncate">{s.description}</p>}
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm font-bold text-[#0F2645] whitespace-nowrap">{eur(s.price_htva)}</p>
                <p className="text-[11px] text-[#93A0BC] whitespace-nowrap">TVA {s.vat_percent} %</p>
              </div>
              <button type="button" data-testid={`service-delete-${s.id}`} onClick={() => remove(s)} className="shrink-0 p-1 text-slate-300 hover:text-red-600 transition-colors duration-200" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] font-medium text-[#93A0BC]">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[#5C6A87]">{s.unit}</span>
              <label className="inline-flex items-center gap-1">
                <span className="text-[#93A0BC]">Coût</span>
                <input
                  data-testid={`service-cost-${s.id}`}
                  type="number"
                  step="0.01"
                  min="0"
                  defaultValue={s.cost_price || 0}
                  onBlur={(e) => saveField(s, "cost_price", e.target.value)}
                  className="w-16 rounded-lg border border-[#E2E7F7] bg-white px-2 py-1 text-right text-[11px] font-semibold text-[#0F2645] focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
                />
                <span className="text-[#93A0BC]">{sym()}</span>
              </label>
              <label className="inline-flex items-center gap-1">
                <span className="text-[#93A0BC]">Durée</span>
                <input
                  data-testid={`service-minutes-${s.id}`}
                  type="number"
                  step="5"
                  min="0"
                  defaultValue={s.duration_minutes || 0}
                  onBlur={(e) => saveField(s, "duration_minutes", e.target.value)}
                  className="w-16 rounded-lg border border-[#E2E7F7] bg-white px-2 py-1 text-right text-[11px] font-semibold text-[#0F2645] focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
                />
                <span className="text-[#93A0BC]">min</span>
              </label>
              {isOwner && (
                <span data-testid={`service-margin-${s.id}`} className={s.cost_price ? "font-bold text-green-700" : ""}>marge {margin(s)}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </StudioLayout>
  );
}
